/// <amd-module name="Artifacts/Root/components/CommandPanel" />
import { CenteredSpinner } from "Artifacts/Root/components/CenteredSpinner";
import { ErrorBannerLoader } from "Artifacts/Root/components/ErrorBannerLoader";
import { FWLink } from "Artifacts/Root/components/FWLink";
import { ComponentState } from "Artifacts/Root/Constants";
import { isLoading } from "Artifacts/Root/helpers/ComponentStateHelpers";
import * as Res from "Artifacts/Root/Resources";
import { IPackageVersionIdentity } from "Artifacts/Root/types";
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { IFocusZoneProps } from "VSSUI/FocusZone";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Panel } from "VSSUI/Panel";
import { ITableColumn, renderSimpleCell, Table } from "VSSUI/Table";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

export interface ICommandPanelProps {
  title: string;
  description: string;
  fwLinkId?: string;
  packages?: IPackageVersionIdentity[];
  onDismiss: () => void;
  onSubmit: (inputParams?: any) => void;
  submitButtonComponentState: ComponentState;
  submitButtonText: string;
  isSubmitButtonDanger?: boolean;
  isSubmitButtonDisabled?: boolean;
  warningText?: string;
  warningTextFWLinkId?: string;
}

const CommandPanel: React.FC<ICommandPanelProps> = ({
  title,
  description,
  fwLinkId,
  packages,
  onDismiss,
  onSubmit,
  submitButtonComponentState,
  submitButtonText,
  isSubmitButtonDanger,
  isSubmitButtonDisabled,
  warningText,
  warningTextFWLinkId,
  children,
}) => {
  return (
    <Panel
      className="command-panel"
      titleProps={{ text: title }}
      lightDismiss={false}
      onDismiss={onDismiss}
      overlayContent={
        isLoading(submitButtonComponentState) ? (
          <CenteredSpinner label={Res.Submitting} />
        ) : undefined
      }
      footerButtonProps={[
        {
          onClick: onDismiss,
          text: Res.Modal_Cancel,
        },
        {
          primary: isSubmitButtonDanger === true ? false : true,
          danger: isSubmitButtonDanger || false,
          onClick: (inputParams: any) => onSubmit(inputParams),
          text: submitButtonText,
          disabled: isSubmitButtonDisabled,
        },
      ]}
    >
      <ErrorBannerLoader state={submitButtonComponentState} noLoadingSpinner>
        <div>
          {description}
          {fwLinkId && <FWLink linkId={fwLinkId}>{Res.LearnMore}</FWLink>}
          {warningText && (
            <MessageCard className="dialog-warning-card" severity={MessageCardSeverity.Warning}>
              {warningText}
              {warningTextFWLinkId && <FWLink linkId={warningTextFWLinkId}>{Res.LearnMore}</FWLink>}
            </MessageCard>
          )}
          <Table
            columns={packageVersionColumns}
            itemProvider={
              new ArrayItemProvider<IPackageVersionIdentity | undefined>(packages || [])
            }
            focuszoneProps={{ disabled: true } as IFocusZoneProps}
            excludeTabStop={true}
          />
          {children}
        </div>
      </ErrorBannerLoader>
    </Panel>
  );
};

const packageVersionColumns = [
  {
    id: "name",
    minWidth: 50,
    name: Res.PackageListTable_TableColumn_Package,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(-100),
  } as ITableColumn<IPackageVersionIdentity>,
  {
    id: "version",
    minWidth: 50,
    name: Res.PackageListTable_TableColumn_Version,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(-60),
  } as ITableColumn<IPackageVersionIdentity>,
];

export { CommandPanel };
